import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaSearch,
  FaBoxOpen,
  FaDollarSign,
  FaExclamationTriangle,
  FaWarehouse,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import '../styles/InventoryStock.css';

const FIND_ALL_ITEMS = `
  query StockLevelItems {
    findAllItem {
      id
      name
      code
      quantity
      available_quantity
      perItemCost
      category
      unit
      reorderLevel
      maxStockLevel
    }
  }
`;

const FIND_ALL_INVENTORY = `
  query StockLevelSites {
    findAllInventoryManagement {
      id
      project { id name }
      tool { id }
    }
  }
`;

const PAGE_SIZE = 8;
const DEFAULT_SITE = 'Main Warehouse';

function stockStatus(onHand, reorder) {
  const qty = Number(onHand) || 0;
  const level = Number(reorder);
  const reorderLevel = Number.isFinite(level) && level > 0 ? level : 5;
  if (qty <= 0) return { key: 'out', label: 'Out of Stock' };
  if (qty <= Math.max(1, Math.round(reorderLevel * 0.4))) {
    return { key: 'critical', label: 'Critical' };
  }
  if (qty <= reorderLevel) return { key: 'low_stock', label: 'Low Stock' };
  return { key: 'in_stock', label: 'In Stock' };
}

function barTone(pct) {
  if (pct <= 0) return 'empty';
  if (pct < 25) return 'low';
  if (pct < 60) return 'mid';
  return 'high';
}

export default function StockLevelsDashboard() {
  const [items, setItems] = useState([]);
  const [sitesByItem, setSitesByItem] = useState({});
  const [search, setSearch] = useState('');
  const [site, setSite] = useState('All');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      let itemsRes;
      try {
        itemsRes = await graphqlRequest(FIND_ALL_ITEMS);
      } catch (err) {
        const msg = String(err?.message || '').toLowerCase();
        if (msg.includes('maxstock')) {
          itemsRes = await graphqlRequest(`
            query StockLevelItemsLegacy {
              findAllItem {
                id name code quantity available_quantity
                perItemCost category unit reorderLevel
              }
            }
          `);
        } else {
          throw err;
        }
      }
      const [mgmtRes] = await Promise.all([
        graphqlRequest(FIND_ALL_INVENTORY).catch(() => ({})),
      ]);
      const list = Array.isArray(itemsRes?.findAllItem) ? itemsRes.findAllItem : [];
      setItems(list);

      const map = {};
      (mgmtRes?.findAllInventoryManagement || []).forEach((rec) => {
        const id = rec?.tool?.id;
        const name = rec?.project?.name;
        if (!id || !name) return;
        if (!map[id]) map[id] = new Set();
        map[id].add(name);
      });
      const plain = {};
      Object.entries(map).forEach(([id, set]) => {
        plain[id] = Array.from(set);
      });
      setSitesByItem(plain);
    } catch (e) {
      toast.error(e?.message || 'Failed to load stock levels');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(
    () =>
      (items || []).map((item) => {
        const onHand = Number(item.available_quantity ?? item.quantity ?? 0);
        const reorder = Number(item.reorderLevel ?? 5) || 5;
        const maxLevel =
          Number(item.maxStockLevel) > 0
            ? Number(item.maxStockLevel)
            : Math.max(reorder * 4, onHand, 1);
        const pct = Math.max(0, Math.min(100, Math.round((onHand / maxLevel) * 100)));
        const locations = sitesByItem[item.id]?.length
          ? sitesByItem[item.id]
          : [DEFAULT_SITE];
        return {
          ...item,
          onHand,
          reorder,
          maxLevel,
          pct,
          status: stockStatus(onHand, reorder),
          location: locations[0],
          unit: item.unit || 'Each',
        };
      }),
    [items, sitesByItem],
  );

  const sites = useMemo(() => {
    const set = new Set(rows.map((r) => r.location).filter(Boolean));
    return ['All', ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (site !== 'All' && r.location !== site) return false;
      if (!q) return true;
      return `${r.name} ${r.code} ${r.location}`.toLowerCase().includes(q);
    });
  }, [rows, search, site]);

  useEffect(() => {
    setPage(1);
  }, [search, site]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const stats = useMemo(() => {
    const value = rows.reduce(
      (sum, r) => sum + r.onHand * (Number(r.perItemCost) || 0),
      0,
    );
    return {
      skus: rows.length,
      value,
      below: rows.filter(
        (r) => r.status.key === 'low_stock' || r.status.key === 'critical' || r.status.key === 'out',
      ).length,
      locations: new Set(rows.map((r) => r.location)).size || 1,
    };
  }, [rows]);

  return (
    <div className="isk-page">
      <header className="isk-header">
        <h1>Stock Levels</h1>
      </header>

      <section className="isk-stats">
        <article className="isk-stat">
          <div className="isk-stat-icon isk-stat-icon--blue">
            <FaBoxOpen />
          </div>
          <div>
            <div className="isk-stat-value">{stats.skus}</div>
            <div className="isk-stat-label">Total SKUs</div>
          </div>
        </article>
        <article className="isk-stat">
          <div className="isk-stat-icon isk-stat-icon--teal">
            <FaDollarSign />
          </div>
          <div>
            <div className="isk-stat-value">{formatAUD(stats.value)}</div>
            <div className="isk-stat-label">Total Stock Value</div>
          </div>
        </article>
        <article className="isk-stat">
          <div className="isk-stat-icon isk-stat-icon--orange">
            <FaExclamationTriangle />
          </div>
          <div>
            <div className="isk-stat-value">{stats.below}</div>
            <div className="isk-stat-label">Below Reorder Level</div>
          </div>
        </article>
        <article className="isk-stat">
          <div className="isk-stat-icon isk-stat-icon--green">
            <FaWarehouse />
          </div>
          <div>
            <div className="isk-stat-value">{stats.locations}</div>
            <div className="isk-stat-label">Locations</div>
          </div>
        </article>
      </section>

      <div className="isk-panel">
        <div className="isk-filters">
          <label className="isk-search">
            <FaSearch />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products..."
            />
          </label>
          <select
            className="isk-select"
            value={site}
            onChange={(e) => setSite(e.target.value)}
          >
            {sites.map((s) => (
              <option key={s} value={s}>
                {s === 'All' ? 'All locations' : s}
              </option>
            ))}
          </select>
          {(search || site !== 'All') && (
            <button
              type="button"
              className="isk-clear"
              onClick={() => {
                setSearch('');
                setSite('All');
              }}
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="isk-table-wrap">
          <table className="isk-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Location</th>
                <th>Current Stock</th>
                <th>Reorder Level</th>
                <th>Max Level</th>
                <th>Stock %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="isk-empty">
                    Loading stock levels…
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="isk-empty">
                    No products found. Add products first, then stock will appear here.
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => (
                  <tr key={row.id}>
                    <td className="isk-name">{row.name}</td>
                    <td>{row.code || '—'}</td>
                    <td>{row.location}</td>
                    <td>
                      {row.onHand} {row.unit}
                    </td>
                    <td>{row.reorder}</td>
                    <td>{row.maxLevel}</td>
                    <td>
                      <span className={`isk-bar isk-bar--${barTone(row.pct)}`}>
                        <i style={{ width: `${row.pct}%` }} />
                      </span>
                      {row.pct}%
                    </td>
                    <td>
                      <span className={`isk-status isk-status--${row.status.key}`}>
                        {row.status.label}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="isk-foot">
          <span>
            Showing {pageRows.length} of {filtered.length} products
          </span>
          <div className="isk-pages">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .slice(0, 8)
              .map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`isk-page-btn ${pageSafe === n ? 'is-active' : ''}`}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
